import {Event} from 'shared/events/Event';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';
import {useCheckProductVariations} from 'components/customer/Confirmation/helpers/useCheckProductVariations';
import {useNotificationContext} from 'contexts';
import {genericMessageHandler} from 'shared/helpers';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {usePaymentSettings} from 'components/customer/Confirmation/helpers/usePaymentSettings';
import {useAppSelector} from 'store/customer';
import {selectHasCustomerOverpaid} from 'components/customer/Confirmation/store/confirmationSlice';
import {useLazyGetJobCostQuery} from 'components/customer/Job/store/jobApi';

export const useGoToPaymentPage = (): Event => {
    const {notify} = useNotificationContext();
    const navigate = useNavigate();
    const {job} = useJobContext() as PartialJob;
    const {isConfirmationPage} = useIsConfirmationPage();
    const {getPaymentSettings} = usePaymentSettings();
    const overPaid = useAppSelector(selectHasCustomerOverpaid);

    const {checkIfThereAreVariations} = useCheckProductVariations();
    const [getJobCost] = useLazyGetJobCostQuery();

    const action = useCallback(async (): Promise<boolean> => {
        if (overPaid) return true;

        if (isConfirmationPage) {
            try {
                const hasVariation = await checkIfThereAreVariations();
                const cost = await getJobCost({jobId: Number(job.id)}).unwrap();

                if (
                    cost?.missingRequiredDeliveryExtraGroups?.length &&
                    job.status == JOB_STATUS.PROCESSING
                ) {
                    return false;
                }

                if (
                    (hasVariation ||
                        cost?.defaultFreightApplies ||
                        cost?.hasIndicativeDeliveryExtras) &&
                    job.status == JOB_STATUS.PROCESSING
                ) {
                    return true;
                }

                const paymentSettings = await getPaymentSettings(20000);

                if (
                    paymentSettings?.allowPayOnAccount ||
                    paymentSettings?.enableCardPayments ||
                    paymentSettings?.enableInvoicePayments
                ) {
                    void navigate(`/v2/job/${job.displayId}/payment`);
                    return false;
                }
            } catch (e) {
                if (e instanceof Error) {
                    genericMessageHandler(notify, e.message);
                }
                return false;
            }
        }

        return true;
    }, [
        job,
        navigate,
        isConfirmationPage,
        checkIfThereAreVariations,
        getJobCost,
        getPaymentSettings,
        notify,
        overPaid,
    ]);

    return {
        action,
    };
};
